import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  estimateSuffix,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { ResourceVerificationRow } from "./use-enforcement.ts";
import {
  deriveVerificationViews,
  quantityMeasure,
  verificationPathLabel,
  verificationCodeLabel,
  verificationHref,
  type VerificationView,
} from "./vocab.ts";

/**
 * Resource Server verification outcomes by code (task 11.8; design 12.2 "Resource Server
 * verification outcomes by code"; 12.3 drill-down; 12.4 semantic table + status icon+text).
 *
 * Reads the summary's authoritative `resource_verification` rows (path + error code + count), orders
 * them by (code, path) through `deriveVerificationViews`, and renders them in the shared semantic
 * `DenseTable`. Each row's count carries its own exact/estimate/unverifiable confidence (design
 * 12.3), and every row drills to the events behind it with an honest standing `/events` filter
 * (`category=verification`, path, code) — the summary attaches no server `drill`.
 *
 * An outcome is shown as a status via `StatusBadge` (icon + text, never colour alone): the `none`
 * code is the verified-success outcome (healthy); any failure code is an error; and a count the
 * server could not verify (null) is grey `unknown`, never green (design 12.3 / 12.4).
 *
 * This is NOT a catalog-complete breakdown: by contract `resource_verification` lists only observed
 * path/code pairs, so an empty window shows the table's honest empty state rather than fabricated
 * zero rows (unlike the replay and Gateway breakdowns, which ARE always present with zeros).
 */

export interface VerificationOutcomesProps {
  readonly rows: readonly ResourceVerificationRow[];
}

export function VerificationOutcomes(props: VerificationOutcomesProps): ReactNode {
  const navigate = useNavigate();
  const views = deriveVerificationViews(props.rows);

  const columns: readonly Column<VerificationView>[] = [
    {
      key: "outcome",
      header: "Outcome",
      isRowHeader: true,
      cell: (v) => {
        const level = v.code === "none" ? "healthy" : "error";
        return (
          <span className="enf-outcome">
            <StatusBadge
              level={level}
              label={verificationCodeLabel(v.code)}
              size="sm"
              title={
                v.code === "none"
                  ? "Verified: the Resource Server accepted the credential."
                  : `Verification failed: ${verificationCodeLabel(v.code).toLowerCase()}.`
              }
            />
          </span>
        );
      },
    },
    {
      key: "path",
      header: "Path",
      cell: (v) => <span className="enf-faint">{verificationPathLabel(v.path)}</span>,
    },
    {
      key: "count",
      header: "Count",
      align: "right",
      cell: (v) => {
        const measure = quantityMeasure(v.count);
        const text =
          v.count.value === null
            ? "n/a"
            : `${defaultNumberFormat(v.count.value)}${estimateSuffix(measure)}`;
        return (
          <button
            type="button"
            className="enf-link enf-num"
            data-measure={measure}
            onClick={() => navigate(verificationHref(v.path, v.code))}
            aria-label={`Open the verification events for ${verificationCodeLabel(
              v.code,
            ).toLowerCase()} ${verificationPathLabel(v.path).toLowerCase()}`}
          >
            {text}
          </button>
        );
      },
    },
  ];

  return (
    <section className="enf-section" aria-label="Resource Server verification outcomes">
      <div className="enf-section__head">
        <h2 className="enf-section__title">Resource Server verification</h2>
      </div>
      <DenseTable
        caption="Resource Server verification outcomes by error code and path"
        columns={columns}
        rows={views}
        rowKey={(v) => `${v.code}:${v.path}`}
        stickyHeader
        emptyMessage="No Resource Server verifications were recorded in this window. Widen the time range, or check that a Resource Server is reporting verification outcomes."
      />
    </section>
  );
}
